import React, { useEffect, useState, useMemo, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import { Aragog, Fluffy, Nagini } from "./Creatures";
import { fetchLC, LCData } from "./leetcodeApi";
import HouseHourglass from "../HouseHourglass/HouseHourglass";

/* ── Palettes — one per house, plus Dumbledore's Army ──
   `C` is a proxy onto whichever is active, so both styled-components and SVG
   attributes read the live palette. Note the styled interpolations below are
   written `${() => C.x}` rather than `${() => C.x}`: styled-components evaluates a
   bare interpolation once at module load, which would freeze the theme. */

export type LcTheme = "gryffindor" | "slytherin" | "ravenclaw" | "hufflepuff" | "da";

interface Palette {
  bg: string; card: string; b: string; p: string; t: string; m: string; d: string;
  easy: string; med: string; hard: string;
  heatmap: string; accent: string; accentDeep: string; gold: string;
  heat: string[];
  frame: string; frameDark: string; frameLight: string;
}

const SHARED = {
  bg: "#161519", t: "#f0e8f0", m: "#8a7a9a", d: "#5a4a6a",
  easy: "#22c55e", med: "#e8a030", hard: "#cc2244",
  gold: "#ffd700",
};

export const LC_THEMES: { key: LcTheme; name: string; crest: string; swatch: string }[] = [
  { key: "gryffindor", name: "Gryffindor", crest: "\u{1F981}", swatch: "#e0474d" },
  { key: "hufflepuff", name: "Hufflepuff", crest: "\u{1F9A1}", swatch: "#f0c249" },
  { key: "ravenclaw",  name: "Ravenclaw",  crest: "\u{1F985}", swatch: "#5aa9ff" },
  { key: "slytherin",  name: "Slytherin",  crest: "\u{1F40D}", swatch: "#2fbd6d" },
  { key: "da",         name: "Dumbledore's Army", crest: "\u{26A1}", swatch: "#a97bff" },
];

const PALETTES: Record<LcTheme, Palette> = {
  gryffindor: {
    ...SHARED, card: "rgba(24,12,14,0.85)", b: "rgba(224,71,77,0.08)", p: "#e0474d",
    heatmap: "#c1272d", accent: "#e0474d", accentDeep: "#a3161c",
    heat: ["#4a1013", "#7d1a20", "#b2262e", "#dd4a50", "#f5868a"],
    frame: "#8a6a20", frameDark: "#3a2818", frameLight: "#d3a625",
  },
  hufflepuff: {
    ...SHARED, card: "rgba(24,20,10,0.85)", b: "rgba(240,194,73,0.08)", p: "#e0a81f",
    heatmap: "#e0a81f", accent: "#f0c249", accentDeep: "#a97c10",
    heat: ["#3d2f08", "#6b5210", "#9c7818", "#d3a52c", "#f7d874"],
    frame: "#6b5a24", frameDark: "#2a2210", frameLight: "#d8bb56",
  },
  ravenclaw: {
    ...SHARED, card: "rgba(14,18,30,0.85)", b: "rgba(90,169,255,0.08)", p: "#5aa9ff",
    heatmap: "#2470e0", accent: "#5aa9ff", accentDeep: "#1e6ce6",
    heat: ["#123a7a", "#1650b4", "#1e6ce6", "#3f92ff", "#78bcff"],
    frame: "#3f5d86", frameDark: "#1b2a42", frameLight: "#6f96c8",
  },
  slytherin: {
    ...SHARED, card: "rgba(10,22,16,0.85)", b: "rgba(47,189,109,0.08)", p: "#2fbd6d",
    heatmap: "#1a7a45", accent: "#2fbd6d", accentDeep: "#12703c",
    heat: ["#0c2f1c", "#12522f", "#1a7a45", "#28a55e", "#5fd692"],
    frame: "#5f6f68", frameDark: "#18241e", frameLight: "#b9c6c0",
  },
  da: {
    ...SHARED, card: "rgba(18,12,28,0.85)", b: "rgba(169,123,255,0.08)", p: "#a97bff",
    heatmap: "#7c4ddb", accent: "#a97bff", accentDeep: "#6d3fd4",
    heat: ["#2a1a4d", "#432a80", "#5f3cb4", "#8558e0", "#b48dff"],
    frame: "#5a4a86", frameDark: "#221a3a", frameLight: "#9d86d6",
  },
};

/* Deliberately not persisted — every visit starts in Ravenclaw and the
   choice lasts only for the session on the page. */
const DEFAULT_THEME: LcTheme = "ravenclaw";
let active: LcTheme = DEFAULT_THEME;

export const setLcTheme = (k: LcTheme) => { active = k; };
export const getLcTheme = () => active;

const C = new Proxy({} as Palette, {
  get: (_t, key: string) => (PALETTES[active] as any)[key],
});


const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* ═══════════════════════ DEATHLY HALLOWS SYMBOL ══════════════════════════ */
const DeathlyHallows: React.FC = () => (
  <DHSvg viewBox="0 0 60 60" fill="none">
    <path d="M30 5 L55 52 L5 52 Z" stroke={C.accent} strokeWidth="1.2" strokeOpacity="0.4" fill="none" />
    <circle cx="30" cy="36" r="13" stroke={C.accent} strokeWidth="1.2" strokeOpacity="0.4" fill="none" />
    <line x1="30" y1="5" x2="30" y2="52" stroke={C.accent} strokeWidth="1.2" strokeOpacity="0.4" />
  </DHSvg>
);

/* ═══════════════════════ WAND SVG ════════════════════════════════════════ */
const WandDecor: React.FC = () => (
  <WandSvg viewBox="0 0 120 20" fill="none">
    <line x1="10" y1="10" x2="100" y2="10" stroke={C.accent} strokeWidth="2" strokeOpacity="0.3" strokeLinecap="round" />
    <circle cx="100" cy="10" r="3" fill={C.accent} fillOpacity="0.4" />
    <circle cx="106" cy="6" r="1" fill={C.accent} fillOpacity="0.3" className="wand-spark" />
    <circle cx="108" cy="12" r="0.8" fill={C.accent} fillOpacity="0.25" className="wand-spark" />
    <circle cx="104" cy="3" r="0.6" fill={C.accent} fillOpacity="0.2" className="wand-spark" />
    <rect x="5" y="8" width="8" height="4" rx="1" fill="#6b5030" fillOpacity="0.6" />
  </WandSvg>
);

/* ═══════════════════════ SORTING HAT SVG ═════════════════════════════════ */
const SortingHatIcon: React.FC = () => (
  <SortHatSvg viewBox="0 0 40 45" fill="none">
    <ellipse cx="20" cy="38" rx="18" ry="5" fill="#3a2818" stroke="#6b5030" strokeWidth="0.8" />
    <path d="M8 38 Q6 25 10 15 Q14 6 18 2 Q22 6 26 12 Q30 18 32 38 Z" fill="#4a3520" stroke="#6b5030" strokeWidth="0.8" />
    <path d="M18 2 Q25 5 28 12" stroke="#6b5030" strokeWidth="0.8" fill="none" />
    <path d="M10 28 Q20 32 30 28" stroke="#6b5030" strokeWidth="0.5" fill="none" opacity="0.5" />
  </SortHatSvg>
);

/* ═══════════════════════ PHILOSOPHER'S STONE ═════════════════════════════ */
const PhilosopherStone: React.FC<{achieved: boolean}> = ({ achieved }) => (
  <PStoneSvg viewBox="0 0 40 40" fill="none" $achieved={achieved}>
    <defs>
      <radialGradient id="stoneGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ff3355" stopOpacity={achieved ? "0.6" : "0.1"} />
        <stop offset="100%" stopColor="#ff3355" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="20" cy="20" r="18" fill="url(#stoneGlow)" />
    <polygon points="20,4 36,20 20,36 4,20" fill="none" stroke={achieved ? "#ff3355" : C.d} strokeWidth="1.2" strokeOpacity={achieved ? "0.7" : "0.3"} />
    <polygon points="20,10 30,20 20,30 10,20" fill={achieved ? "#ff3355" : C.d} fillOpacity={achieved ? "0.2" : "0.05"} />
    <circle cx="20" cy="20" r="4" fill={achieved ? "#ff3355" : C.d} fillOpacity={achieved ? "0.5" : "0.1"} />
  </PStoneSvg>
);

/* ═══════════════════════ MOVING STAIRCASE ════════════════════════════════ */
const MovingStaircase: React.FC<{flip?: boolean}> = ({ flip }) => {
  /* A connected flight of stairs rather than free-floating slabs: each tread
     meets the next by a riser, and one banister runs the whole length. */
  const STEPS = 16, W = 950, treadW = W / STEPS, rise = 2.2;
  const baseY = 30;
  let path = `M 0 ${baseY}`;
  const posts: { x: number; y: number }[] = [];
  for (let i = 0; i < STEPS; i++) {
    const x = i * treadW;
    const y = baseY - i * rise;
    path += ` L ${x} ${y} L ${x + treadW} ${y}`;
    posts.push({ x: x + treadW * 0.5, y });
  }
  const railTop = (x: number) => baseY - (x / treadW) * rise - 13;

  return (
    <StaircaseSvg viewBox="0 0 950 44" $flip={flip} aria-hidden="true">
      <g className="stair-group">
        {/* treads + risers, filled down to the base so it reads as solid stone */}
        <path d={`${path} L ${W} 44 L 0 44 Z`} fill={C.frameDark} fillOpacity="0.16" />
        <path d={path} fill="none" stroke={C.frame} strokeWidth="1.5" strokeOpacity="0.7" />
        {/* candlelight catching each tread edge */}
        <path d={path} fill="none" stroke={C.frameLight} strokeWidth="0.8" strokeOpacity="0.45" />

        {/* banister */}
        <path d={`M 0 ${railTop(0)} L ${W} ${railTop(W)}`}
          stroke={C.frame} strokeWidth="1.4" strokeOpacity="0.45" fill="none" />
        {posts.map((p, i) => (
          <g key={i}>
            <rect x={p.x} y={railTop(p.x)} width="1.6" height={p.y - railTop(p.x)}
              fill={C.frame} fillOpacity="0.35" />
            <circle cx={p.x + 0.8} cy={railTop(p.x) - 1.6} r="1.6"
              fill={C.frameLight} fillOpacity="0.3" />
          </g>
        ))}
      </g>

      {/* a torch burning at each end of the corridor */}
      {[14, 936].map((x) => (
        <g key={x}>
          <rect x={x - 1} y="20" width="2.5" height="14" fill={C.frame} fillOpacity="0.5" />
          <circle cx={x} cy="16" r="5.5" fill={C.accent} fillOpacity="0.16" className="torch-glow" />
          <circle cx={x} cy="16" r="2" fill={C.accent} fillOpacity="0.5" className="torch-glow" />
        </g>
      ))}
    </StaircaseSvg>
  );
};

/* ═══════════════════════ PORTRAIT FRAME (Animated Painting) ═════════════ */
const PortraitFrame: React.FC<{title: string; children: React.ReactNode}> = ({ title, children }) => (
  <FrameOuter>
    {/* Ornate top */}
    <FrameTop>
      <FrameCorner $pos="left" />
      <FrameTitle>{title}</FrameTitle>
      <FrameCorner $pos="right" />
    </FrameTop>
    <FrameInner>
      {children}
    </FrameInner>
    <FrameBottom />
  </FrameOuter>
);

/* ═══════════════════════ PATRONUS GUARDIAN SHIELD ═════════════════════════ */
const PatronusShield: React.FC<{badge: string; rating: number; topPct: number}> = ({ badge, rating, topPct }) => (
  <ShieldWrap>
    <ShieldSvg viewBox="0 0 120 140" fill="none">
      <defs>
        <radialGradient id="shieldGlow" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor={C.gold} stopOpacity="0.3" />
          <stop offset="100%" stopColor={C.gold} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="shieldFill" x1="60" y1="0" x2="60" y2="140">
          <stop offset="0%" stopColor="#e8a030" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#e8a030" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#shieldGlow)" />
      <path d="M60 8 L105 30 L105 75 Q105 110 60 132 Q15 110 15 75 L15 30 Z"
        fill="url(#shieldFill)" stroke={C.gold} strokeWidth="1.5" strokeOpacity="0.4" />
      <path d="M60 18 L95 36 L95 72 Q95 102 60 122 Q25 102 25 72 L25 36 Z"
        fill="none" stroke={C.gold} strokeWidth="0.5" strokeOpacity="0.2" />
      <path d="M60 35 L64 50 L80 50 L67 59 L72 74 L60 65 L48 74 L53 59 L40 50 L56 50 Z"
        fill={C.gold} fillOpacity="0.3" stroke={C.gold} strokeWidth="0.5" strokeOpacity="0.5" />
      <text x="60" y="98" textAnchor="middle" fill={C.gold} fontSize="10" fontFamily="Cinzel" fontWeight="700" opacity="0.8">{badge.toUpperCase()}</text>
    </ShieldSvg>
    <ShieldMeta>
      <ShieldRating>{rating.toLocaleString()}</ShieldRating>
      <ShieldSubtitle>Max Contest Rating</ShieldSubtitle>
      <ShieldPct>Top {topPct}% worldwide</ShieldPct>
    </ShieldMeta>
  </ShieldWrap>
);

/* ═══════════════════════ HEATMAP (GitHub / LeetCode style) ═══════════════ */
const HeatmapChart: React.FC<{cal: Record<string,number>; year: number}> = ({ cal, year }) => {
  const { cells, monthLabels, maxCount } = useMemo(() => {
    const days: { x: number; y: number; count: number; date: string }[] = [];
    const counts = Object.values(cal).filter(v => v > 0);
    const max = Math.max(8, ...counts);
    const cellSize = 11, gap = 2, step = cellSize + gap;
    const months: { label: string; x: number }[] = [];
    let lastMonth = -1;

    const DAY = 86400000;
    const jan1 = Date.UTC(year, 0, 1);
    const now = new Date();
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    // current year stops at today; past years run to 31 Dec
    const end = Math.min(Date.UTC(year, 11, 31), todayUTC);

    /* Columns must line up with real Sun–Sat weeks. Deriving the column from
       `index / 7` instead skews the whole grid whenever 1 Jan isn't a Sunday. */
    const gridStart = jan1 - new Date(jan1).getUTCDay() * DAY;

    for (let t = gridStart; t <= end; t += DAY) {
      const d = new Date(t);
      const week = Math.round((t - gridStart) / (7 * DAY));
      if (t < jan1) continue;                      // leading padding to the first Sunday
      const count = cal[String(Math.floor(t / 1000))] || 0;
      days.push({
        x: week * step,
        y: d.getUTCDay() * step,
        count,
        date: d.toISOString().slice(0, 10),
      });
      if (d.getUTCMonth() !== lastMonth) {
        lastMonth = d.getUTCMonth();
        months.push({ label: MONTHS[d.getUTCMonth()], x: week * step });
      }
    }
    return { cells: days, monthLabels: months, maxCount: max };
  }, [cal, year]);

  const [tip, setTip] = useState<{x:number;y:number;date:string;count:number} | null>(null);
  const W = 53 * 13;
  return (
   <HmHolder>
    {tip && (
      <HeatTip style={{ left: tip.x, top: tip.y }}>
        <b>{tip.count}</b> submission{tip.count === 1 ? "" : "s"}
        <em>{new Date(tip.date + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}</em>
      </HeatTip>
    )}
    <HmSvg viewBox={`0 0 ${W} 110`}>
      {cells.map((c, i) => (
        <rect key={i} x={c.x} y={c.y} width="11" height="11" rx="5.5" ry="5.5"
          fill={c.count === 0
            ? "rgba(255,255,255,0.035)"
            : C.heat[Math.min(C.heat.length - 1, Math.floor((c.count / maxCount) * C.heat.length))]}
          style={{ cursor: "none" }}
          onMouseEnter={(e) => {
            const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
            const box = e.currentTarget.getBoundingClientRect();
            setTip({
              x: box.left - r.left + box.width / 2,
              y: box.top - r.top,
              date: c.date,
              count: c.count,
            });
          }}
          onMouseLeave={() => setTip(null)}
        />
      ))}
      {monthLabels.map((m, i) => (
        <text key={i} x={m.x} y={105} fill={C.d} fontSize="9" fontFamily="JetBrains Mono">{m.label}</text>
      ))}
    </HmSvg>
   </HmHolder>
  );
};

/* ═══════════════════════ CONTEST RATING CHART ════════════════════════════ */
interface ChartHover {
  i: number; px: number; py: number; title: string; rating: number;
  ranking: number; solved: number; total: number; startTime: number;
}

const ContestChart: React.FC<{history: LCData["contestHistory"]}> = ({ history }) => {
  const [hover, setHover] = useState<ChartHover | null>(null);
  if (history.length < 2) return null;
  const W = 600, H = 120;
  const ratings = history.map(h => h.rating);
  const minR = Math.floor((Math.min(...ratings) - 50) / 100) * 100;
  const maxR = Math.ceil((Math.max(...ratings) + 50) / 100) * 100;
  const timeMin = history[0].startTime;
  const timeMax = history[history.length - 1].startTime;
  const tRange = timeMax - timeMin || 1;

  const toX = (time: number) => ((time - timeMin) / tRange) * W;
  const toY = (r: number) => H - ((r - minR) / (maxR - minR)) * H;

  const linePts = history.map(h => `${toX(h.startTime)},${toY(h.rating)}`).join(" ");
  const areaPts = `0,${H} ${linePts} ${W},${H}`;

  const last = history[history.length - 1];
  const maxEntry = history.reduce((best, h) => h.rating > best.rating ? h : best, history[0]);
  const startYear = new Date(timeMin * 1000).getFullYear();
  const endYear = new Date(timeMax * 1000).getFullYear();

  return (
    <ChartWrap>
      {hover && (
        <ChartTip style={{ left: `${hover.px}%`, top: hover.py }}>
          <strong>{hover.title}</strong>
          Rating <span>{Math.round(hover.rating)}</span> &middot; Rank <span>{hover.ranking.toLocaleString()}</span>
          <br />{hover.solved}/{hover.total} solved &middot; {new Date(hover.startTime * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
        </ChartTip>
      )}
      <svg viewBox={`-5 -15 ${W + 10} ${H + 35}`} style={{ width: "100%", height: 150 }}>
        <defs>
          <linearGradient id="ratingGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.accent} stopOpacity="0.18" />
            <stop offset="100%" stopColor={C.accent} stopOpacity="0" />
          </linearGradient>
          <filter id="goldGlow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <polygon points={areaPts} fill="url(#ratingGrad)" />
        <polyline points={linePts} fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Max rating — golden star */}
        <circle cx={toX(maxEntry.startTime)} cy={toY(maxEntry.rating)} r="6"
          fill={C.accent} stroke={C.bg} strokeWidth="2" filter="url(#goldGlow)" />
        <text x={toX(maxEntry.startTime)} y={toY(maxEntry.rating) - 10}
          textAnchor="middle" fill={C.accent} fontSize="8" fontFamily="Cinzel" fontWeight="700">
          {Math.round(maxEntry.rating)}
        </text>
        {/* Current endpoint */}
        <circle cx={toX(last.startTime)} cy={toY(last.rating)} r="4"
          fill="#3b82f6" stroke={C.bg} strokeWidth="2" />
        {/* invisible hit targets, one per contest */}
        {history.map((h, i) => (
          <g key={i}>
            {hover?.i === i && (
              <circle cx={toX(h.startTime)} cy={toY(h.rating)} r="4.5"
                fill="none" stroke={C.accent} strokeWidth="1.5" />
            )}
            <circle cx={toX(h.startTime)} cy={toY(h.rating)} r="9"
              fill="transparent" style={{ cursor: "none" }}
              onMouseEnter={() => setHover({
                i,
                px: (toX(h.startTime) / W) * 100,
                py: toY(h.rating),
                title: h.title, rating: h.rating, ranking: h.ranking,
                solved: h.problemsSolved, total: h.totalProblems, startTime: h.startTime,
              })}
              onMouseLeave={() => setHover(null)}
            />
          </g>
        ))}
        <text x="0" y={H + 16} fill={C.d} fontSize="9" fontFamily="JetBrains Mono">{startYear}</text>
        <text x={W} y={H + 16} textAnchor="end" fill={C.d} fontSize="9" fontFamily="JetBrains Mono">{endYear}</text>
      </svg>
    </ChartWrap>
  );
};

/* ═══════════════════════ TOP% BAR HISTOGRAM ══════════════════════════════ */
const TopHistogram: React.FC<{topPct: number; rating: number}> = ({ topPct, rating }) => {
  /* The curve is an illustrative bell, not LeetCode's real distribution — but
     the bands and the highlighted bar are anchored to actual numbers. */
  const bars = [2,4,6,10,16,22,30,38,34,26,20,14,10,7,5,3,2,1,1,1];
  const [hi, setHi] = useState<number | null>(null);
  const maxH = Math.max(...bars);
  const LO = 1300, HIGH = 2700;
  const band = (HIGH - LO) / bars.length;
  const highlightIdx = Math.min(bars.length - 1, Math.max(0, Math.floor((rating - LO) / band)));

  return (
    <HistoWrap>
      {hi !== null && (
        <HistoTip style={{ left: `${((hi + 0.5) / bars.length) * 100}%` }}>
          {hi === highlightIdx ? (
            <><strong>You are here</strong>Rating <span>{Math.round(rating)}</span> &middot; top <span>{topPct}%</span></>
          ) : (
            <><strong>{Math.round(LO + hi * band)}&ndash;{Math.round(LO + (hi + 1) * band)}</strong>rating band</>
          )}
        </HistoTip>
      )}
      {bars.map((h, i) => (
        <HistoBar key={i}
          onMouseEnter={() => setHi(i)}
          onMouseLeave={() => setHi(null)}
          style={{
            height: `${(h / maxH) * 100}%`,
            background: i === highlightIdx ? C.accent : i >= highlightIdx ? C.accentDeep : "rgba(255,255,255,0.08)",
            opacity: hi === null || hi === i ? 1 : 0.55,
          }} />
      ))}
    </HistoWrap>
  );
};

/* ═══════════════════════ POTION FLASK SVG ════════════════════════════════ */
const PotionFlask: React.FC<{color: string; solved: number; total: number; label: string}> = ({ color, solved, total, label }) => {
  const fillPct = Math.min(1, solved / total);
  const fillY = 55 - fillPct * 30;
  return (
    <PotionWrap>
      <svg viewBox="0 0 50 70" width="50" height="70">
        <defs>
          <clipPath id={`flask-${label}`}>
            <path d="M15 25 L10 55 Q10 62 18 62 L32 62 Q40 62 40 55 L35 25 Z" />
          </clipPath>
        </defs>
        <rect x="20" y="8" width="10" height="17" rx="2" fill="rgba(255,255,255,0.06)" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
        <path d="M15 25 L10 55 Q10 62 18 62 L32 62 Q40 62 40 55 L35 25 Z"
          fill="rgba(255,255,255,0.03)" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" />
        <rect x="0" y={fillY} width="50" height={62 - fillY} fill={color} fillOpacity="0.25"
          clipPath={`url(#flask-${label})`} />
        <circle cx="22" cy={fillY + 8} r="1.5" fill={color} fillOpacity="0.4" className="potion-bubble" />
        <circle cx="30" cy={fillY + 14} r="1" fill={color} fillOpacity="0.3" className="potion-bubble" />
        <rect x="19" y="5" width="12" height="5" rx="1.5" fill="#6b5030" />
      </svg>
      <PotionLabel $c={color}>{label}</PotionLabel>
      <PotionCount>{solved}<PotionTotal>/{total}</PotionTotal></PotionCount>
    </PotionWrap>
  );
};

/* ═══════════════════════ HORCRUX MILESTONES ══════════════════════════════ */
const HORCRUXES = [
  { name: "Tom Riddle's Diary", icon: "📓", desc: "First 100 problems", threshold: 100 },
  { name: "Marvolo's Ring", icon: "💍", desc: "250 problems conquered", threshold: 250 },
  { name: "Slytherin's Locket", icon: "🐍", desc: "Easy > 100", threshold: -1 },
  { name: "Hufflepuff's Cup", icon: "🏆", desc: "500 problems solved", threshold: 500 },
  { name: "Ravenclaw's Diadem", icon: "👑", desc: "100 Hard problems", threshold: -2 },
  { name: "Nagini", icon: "🐉", desc: "Guardian achieved", threshold: -3 },
  { name: "The Boy Who Lived", icon: "⚡", desc: "700+ mastered", threshold: 700 },
];

const TRIALS = [
  { name: "Fluffy's Chamber", icon: "🐕", desc: "Survive 3-headed beast: 50 contests", check: (d: LCData) => d.contestAttended >= 50 },
  { name: "Aragog's Lair", icon: "🕷️", desc: "Face the spiders: 150 Medium", check: (d: LCData) => d.mediumSolved >= 150 },
  { name: "Half-Blood Prince", icon: "📖", desc: "Master 5+ languages", check: (d: LCData) => d.languages.length >= 5 },
  { name: "Voldemort", icon: "💀", desc: "Defeat Dark Lord: Rating > 2200", check: (d: LCData) => d.contestRating > 2200 },
  { name: "Philosopher's Stone", icon: "💎", desc: "Immortality: 25+ day streak", check: (d: LCData) => d.maxStreak >= 25 },
  { name: "Deathly Hallows", icon: "△", desc: "Master of Death: all 3 > 100", check: (d: LCData) => d.easySolved > 100 && d.mediumSolved > 100 && d.hardSolved > 100 },
];

const HorcruxRow: React.FC<{totalSolved: number; easySolved: number; hardSolved: number; isGuardian: boolean}> = ({ totalSolved, easySolved, hardSolved, isGuardian }) => {
  const isDestroyed = (idx: number) => {
    const h = HORCRUXES[idx];
    if (idx === 2) return easySolved > 100;
    if (idx === 4) return hardSolved >= 100;
    if (idx === 5) return isGuardian;
    return totalSolved >= h.threshold;
  };
  return (
    <HorcruxGrid>
      {HORCRUXES.map((h, i) => {
        const done = isDestroyed(i);
        return (
          <HorcruxItem key={i} $done={done}>
            <HorcruxIcon $done={done}>{h.icon}</HorcruxIcon>
            <HorcruxName $done={done}>{h.name}</HorcruxName>
            <HorcruxDesc>{h.desc}</HorcruxDesc>
            {done && <HorcruxCheck>DESTROYED</HorcruxCheck>}
          </HorcruxItem>
        );
      })}
    </HorcruxGrid>
  );
};

const TrialsRow: React.FC<{data: LCData}> = ({ data }) => (
  <HorcruxGrid>
    {TRIALS.map((t, i) => {
      const done = t.check(data);
      return (
        <HorcruxItem key={i} $done={done}>
          <HorcruxIcon $done={done}>{t.icon}</HorcruxIcon>
          <HorcruxName $done={done}>{t.name}</HorcruxName>
          <HorcruxDesc>{t.desc}</HorcruxDesc>
          {done && <HorcruxCheck>CONQUERED</HorcruxCheck>}
        </HorcruxItem>
      );
    })}
  </HorcruxGrid>
);

/* ═══════════════════════ BEST 5 CONTESTS TABLE ══════════════════════════ */
const BestContests: React.FC<{history: LCData["contestHistory"]}> = ({ history }) => {
  if (history.length === 0) return null;
  const best5 = [...history].sort((a, b) => a.ranking - b.ranking).slice(0, 5);
  return (
    <BestTable>
      <thead>
        <tr>
          <BTh>#</BTh>
          <BTh>Contest</BTh>
          <BTh>Rank</BTh>
          <BTh>Rating</BTh>
          <BTh>Solved</BTh>
        </tr>
      </thead>
      <tbody>
        {best5.map((c, i) => (
          <BTr key={i} $isFirst={i === 0}>
            <BTd $rank={i}><Medal $rank={i}>{i + 1}</Medal></BTd>
            <BTd $rank={i}>{c.title.replace("Weekly Contest ", "WC ").replace("Biweekly Contest ", "BWC ")}</BTd>
            <BTd $rank={i}>{c.ranking.toLocaleString()}</BTd>
            <BTd $rank={i}>{Math.round(c.rating)}</BTd>
            <BTd $rank={i}>{c.problemsSolved}/{c.totalProblems}</BTd>
          </BTr>
        ))}
      </tbody>
    </BestTable>
  );
};

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════════════════ */

const LeetCode: React.FC = () => {
  // Re-rendering with a new key is what makes styled-components regenerate
  // the themed classes; the palette itself lives in the module-level proxy.
  const [theme, setTheme] = useState<LcTheme>(() => {
    setLcTheme(DEFAULT_THEME);
    return DEFAULT_THEME;
  });
  const pickTheme = useCallback((k: LcTheme) => { setLcTheme(k); setTheme(k); }, []);

  const [data, setData] = useState<LCData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLC().then(d => { setData(d); setLoading(false); }); }, []);

  if (loading) return <Section><LoadBox><Spin />Summoning from the shadows...</LoadBox></Section>;
  if (!data) return null;

  const d = data;
  const maxRating = d.contestHistory.length > 0 ? Math.max(...d.contestHistory.map(c => c.rating)) : d.contestRating;
  const bestRank = d.contestHistory.length > 0 ? Math.min(...d.contestHistory.map(c => c.ranking)) : 391;
  const worstRank = d.contestHistory.length > 0 ? Math.max(...d.contestHistory.map(c => c.ranking)) : 13515;
  const avgSolved = d.contestAttended > 0 ? (d.contestHistory.reduce((a, c) => a + c.problemsSolved, 0) / d.contestAttended).toFixed(2) : "0";

  const monthCounts: Record<string, number> = {};
  Object.entries(d.calendar).forEach(([ts, count]) => {
    if (count > 0) {
      const dt = new Date(Number(ts) * 1000);
      const key = `${MONTHS[dt.getMonth()]}'${String(dt.getFullYear()).slice(2)}`;
      monthCounts[key] = (monthCounts[key] || 0) + count;
    }
  });
  const mostActiveMonth = Object.entries(monthCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

  const calValues = Object.values(d.calendar).filter(v => v > 0);
  const avgPerDay = calValues.length > 0 ? (calValues.reduce((a, b) => a + b, 0) / calValues.length).toFixed(1) : "0";
  const maxDay = calValues.length > 0 ? Math.max(...calValues) : 0;

  const isGuardian = d.contestBadge?.toLowerCase() === "guardian";

  return (
    <Section id="leetcode">
      {/* Vertical rails — live figures rather than decoration.
          Held inside a centred 1280px box so they track the content column
          instead of drifting out to the viewport edges on wide screens. */}
      <ThemeBar>
        <ThemeBarLabel>Choose your house</ThemeBarLabel>
        <ThemeBarRow>
          {LC_THEMES.map((t) => (
            <HouseBtn key={t.key} $on={theme === t.key} $swatch={t.swatch}
              onClick={() => pickTheme(t.key)} title={t.name} aria-label={t.name}
              aria-pressed={theme === t.key}>
              <HouseCrest>{t.crest}</HouseCrest>
              <HouseName>{t.name}</HouseName>
            </HouseBtn>
          ))}
        </ThemeBarRow>
      </ThemeBar>

      <Rails aria-hidden="true">
      <RailL>
        <RailWord>The Duelling Club</RailWord>
        <RailRule />
        <RailStat><b>{d.totalSolved}</b> problems felled</RailStat>
        <RailStat><b>{d.contestAttended}</b> duels fought</RailStat>
      </RailL>
      <RailR>
        <RailStat><b>{Math.round(maxRating)}</b> peak rating</RailStat>
        <RailStat><b>top {d.contestTop}%</b> worldwide</RailStat>
        <RailRule />
        <RailWord $accent>Constant vigilance</RailWord>
      </RailR>
      </Rails>

      <Mist $p="tl" /><Mist $p="br" />
      <Inner>

        {/* ═══ HEADER ═══ */}
        <HeaderArea>
          <Oath>I solemnly swear that I am up to no good</Oath>
          <DeathlyHallows />
          <PageTitle>The Wizard's Arena</PageTitle>
          <PageSub>Where algorithms meet incantations</PageSub>
          <WandDecor />
        </HeaderArea>

        {/* ═══ GUARDIAN PATRONUS + TOP% ═══ */}
        <GuardianRow>
          <PatronusShield badge={d.contestBadge} rating={Math.round(maxRating)} topPct={d.contestTop} />
          <TopCard>
            <TopLabel>Expecto Patronum — Top</TopLabel>
            <TopValue>{d.contestTop}%</TopValue>
            <TopPctNote>of all wizards worldwide</TopPctNote>
            <TopHistogram topPct={d.contestTop} rating={maxRating} />
          </TopCard>
        </GuardianRow>

        <MovingStaircase />

        {/* ═══ PROFILE + CONTEST CHART ═══ */}
        <TopGrid>
          <Sidebar>
            <PTop>
              {d.avatar && <Ava src={d.avatar} alt={d.realName} />}
              <div>
                <PName>{d.realName} <V>●</V></PName>
                <PUser>{d.username}</PUser>
                <PRank>Rank <b>{d.ranking.toLocaleString()}</b></PRank>
              </div>
            </PTop>
            <PAbout>{d.aboutMe}</PAbout>
            <PMeta>
              <Mi>📍 {d.country}</Mi>
              {d.website && <Mi>🌐 <a href={d.website} target="_blank" rel="noopener noreferrer">{d.website.replace("https://","")}</a></Mi>}
              <Mi>🐙 {d.github}</Mi>
            </PMeta>

            <SH>Languages</SH>
            {d.languages.map(l => <LR key={l.name}><LT>{l.name}</LT>{l.count} solved</LR>)}

            {d.advSkills.length > 0 && <>
              <SH>Spells Mastered</SH>
              <SL>• Advanced</SL>
              <STags>{d.advSkills.map(s => <ST key={s.name}>{s.name} x{s.count}</ST>)}</STags>
              {d.intSkills.length > 0 && <><SL>• Intermediate</SL><STags>{d.intSkills.map(s => <ST key={s.name}>{s.name} x{s.count}</ST>)}</STags></>}
            </>}
          </Sidebar>

          <RCol>
            <Cd>
              <CRow>
                <CB><CLabel>Contest Rating</CLabel><Big>{d.contestRating.toLocaleString()}</Big></CB>
                <CB><CLabel>Level</CLabel><Guard $isGuardian={isGuardian}>{d.contestBadge}</Guard></CB>
                <CB><CLabel>Global Ranking</CLabel><CVal>{d.contestGlobalRank.toLocaleString()}<Sub>/{d.contestTotal.toLocaleString()}</Sub></CVal></CB>
                <CB><CLabel>Attended</CLabel><CVal>{d.contestAttended}</CVal></CB>
              </CRow>
              <ContestChart history={d.contestHistory} />
            </Cd>
          </RCol>
        </TopGrid>

        <MovingStaircase flip />

        {/* ═══ POTIONS — Sorted by The Sorting Hat ═══ */}
        <SecHead><SortingHatIcon /> The Sorting Hat Sorts — Potions Brewed</SecHead>
        <R2>
          <Cd>
            <SolvedFlex>
              <svg width="150" height="150" viewBox="0 0 150 150">
                <circle cx="75" cy="75" r="58" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="14" />
                <DArc cx="75" cy="75" r="58" fill="none" strokeWidth="14" stroke={C.easy}
                  strokeDasharray={`${(d.easySolved/d.totalAll)*364} 364`} strokeDashoffset="0" />
                <DArc cx="75" cy="75" r="58" fill="none" strokeWidth="14" stroke={C.med}
                  strokeDasharray={`${(d.mediumSolved/d.totalAll)*364} 364`}
                  strokeDashoffset={`${-(d.easySolved/d.totalAll)*364}`} />
                <DArc cx="75" cy="75" r="58" fill="none" strokeWidth="14" stroke={C.hard}
                  strokeDasharray={`${(d.hardSolved/d.totalAll)*364} 364`}
                  strokeDashoffset={`${-((d.easySolved+d.mediumSolved)/d.totalAll)*364}`} />
                <text x="75" y="65" textAnchor="middle" fill={C.t} fontSize="24" fontWeight="700" fontFamily="Cinzel">{d.totalSolved}</text>
                <text x="75" y="82" textAnchor="middle" fill={C.d} fontSize="10" fontFamily="JetBrains Mono">/{d.totalAll}</text>
                <text x="75" y="98" textAnchor="middle" fill={C.easy} fontSize="9" fontFamily="JetBrains Mono">Mastered</text>
              </svg>
              <PotionRow>
                <PotionFlask color={C.easy} solved={d.easySolved} total={d.totalEasy} label="Easy" />
                <PotionFlask color={C.med} solved={d.mediumSolved} total={d.totalMedium} label="Medium" />
                <PotionFlask color={C.hard} solved={d.hardSolved} total={d.totalHard} label="Hard" />
              </PotionRow>
            </SolvedFlex>
          </Cd>
          <Cd>
            <BadgeHeader>Badges <b>{d.badges.length}</b><BadgeArrow>→</BadgeArrow></BadgeHeader>
            <BRow>{d.badges.slice(0,4).map((b,i) => <BI key={i}>{b.icon ? <img src={b.icon} alt={b.name}/> : <span>🛡</span>}</BI>)}</BRow>
            <BR>Most Recent Badge: <b>{d.badges[0]?.name||"Guardian"}</b></BR>
          </Cd>
        </R2>

        <MovingStaircase />

        {/* ═══ BEST 5 SPELLCASTS ═══ */}
        <SecHead>Top 5 Spellcasts — Best Contest Performances</SecHead>
        <Lair><Aragog /></Lair>
        <Cd style={{marginBottom:"1rem"}}>
          <BestContests history={d.contestHistory} />
        </Cd>

        {/* ═══ CONTEST STATS in Portrait Frames ═══ */}
        <R2>
          <PortraitFrame title="Duel Statistics">
            {/* House hourglasses — the Great Hall keeps score of the duels */}
            <GlassRow>
              <HouseHourglass
                house="gryffindor"
                fill={Math.min(100, (maxRating / 2500) * 100)}
                value={Math.round(maxRating)}
                label="Max Rating"
                sub={d.contestBadge}
              />
              <HouseHourglass
                house="ravenclaw"
                fill={Math.max(0, Math.min(100, 100 - bestRank / 20))}
                value={bestRank}
                label="Best Rank"
                sub="of 40K+ duellists"
                delay={0.1}
              />
              <HouseHourglass
                house="slytherin"
                fill={Math.min(100, (d.contestAttended / 50) * 100)}
                value={d.contestAttended}
                label="Duels Fought"
                sub="contests attended"
                delay={0.2}
              />
              <HouseHourglass
                house="hufflepuff"
                fill={Math.max(0, Math.min(100, 100 - d.contestTop))}
                value={`${d.contestTop}%`}
                label="Global Standing"
                sub="top percentile"
                delay={0.3}
              />
            </GlassRow>

            {/* The hourglasses above already carry max rating, best rank and
                duels fought — only the figures they don't show go here. */}
            <SG>
              <SI><StatNum $c={C.p}>{avgSolved}</StatNum><StatLabel>Avg Solved</StatLabel></SI>
              <SI><StatNum $c={C.hard}>{worstRank.toLocaleString()}</StatNum><StatLabel>Worst Rank</StatLabel></SI>
              <SI><StatNum $c={C.d}>{mostActiveMonth}</StatNum><StatLabel>Most Active</StatLabel></SI>
            </SG>
          </PortraitFrame>
          <PortraitFrame title="Kill Count">
            <SG>
              <SI><StatNum $c={C.accent}>{d.contestHistory.filter(c=>c.problemsSolved===c.totalProblems).length} <small>times</small></StatNum><StatLabel>All Killed</StatLabel></SI>
              <SI><StatNum $c={C.p}>{d.contestHistory.filter(c=>c.problemsSolved===3).length} <small>times</small></StatNum><StatLabel>3 solved</StatLabel></SI>
            </SG>
            <SG>
              <SI><StatNum $c={C.p}>{d.contestHistory.filter(c=>c.problemsSolved===2).length} <small>times</small></StatNum><StatLabel>2 solved</StatLabel></SI>
              <SI><StatNum $c={C.p}>{d.contestHistory.filter(c=>c.problemsSolved===1).length} <small>times</small></StatNum><StatLabel>1 solved</StatLabel></SI>
              <SI><StatNum $c={C.d}>{d.contestHistory.filter(c=>c.problemsSolved===0).length}</StatNum><StatLabel>Blanked</StatLabel></SI>
            </SG>
          </PortraitFrame>
        </R2>

        <MovingStaircase flip />

        {/* ═══ HEATMAP — The Marauder's Map ═══ */}
        <SecHead>The Marauder's Map — Submission Activity</SecHead>
        <SerpentTrail><Nagini duration={30} /></SerpentTrail>
        <Cd style={{marginBottom:"0.5rem"}}>
          <HH>
            <span><b>{d.totalSubmissions.toLocaleString()}</b> submissions all time</span>
            <HMeta>Active days: {d.activeDays} · Max streak: {d.maxStreak} · Avg/day: {avgPerDay} · Peak: {maxDay}</HMeta>
          </HH>
          {[...(d.activeYears || [])].sort((a, b) => b - a).map((y) => (
            <YearBlock key={y}>
              <YearLabel>{y}</YearLabel>
              <HeatmapChart cal={d.calendar} year={y} />
            </YearBlock>
          ))}
          <HeatLegend>
            <span>Less</span>
            {["rgba(255,255,255,0.035)", ...C.heat].map((bg, i) => (
              <HeatBox key={i} style={{ background: bg }} />
            ))}
            <span>More</span>
          </HeatLegend>
        </Cd>

        <MovingStaircase />

        {/* ═══ HORCRUXES ═══ */}
        <SecHead>The Seven Horcruxes — Milestones to Destroy</SecHead>
        <Cd style={{marginBottom:"1rem"}}>
          <HorcruxRow totalSolved={d.totalSolved} easySolved={d.easySolved} hardSolved={d.hardSolved} isGuardian={isGuardian} />
        </Cd>

        {/* ═══ TRIALS ═══ */}
        <SecHead>Trials of Hogwarts — Beyond the Horcruxes</SecHead>
        <FluffyPerch><Fluffy /><PerchNote>Fluffy guards the trapdoor</PerchNote></FluffyPerch>
        <TrialsBanner>
          <PhilosopherStone achieved={d.maxStreak >= 25} />
          <TrialsBannerText>
            From Fluffy's three heads to Voldemort's final stand — each trial forges a stronger wizard
          </TrialsBannerText>
        </TrialsBanner>
        <Cd style={{marginBottom:"1rem"}}>
          <TrialsRow data={d} />
        </Cd>

        {/* ═══ CTA ═══ */}
        <CTARow>
          <Btn href={`https://leetcode.com/u/${d.username}/`} target="_blank" rel="noopener noreferrer">View on LeetCode →</Btn>
          <Btn href="https://codolio.com/profile/jugshaurya" target="_blank" rel="noopener noreferrer" $s>Codolio All Platforms →</Btn>
        </CTARow>
        <Live>{d.isLive ? "● Live from LeetCode" : "○ From today's cache"} · refreshed every 3 hours</Live>
      </Inner>
    </Section>
  );
};

export default LeetCode;

/* ═══════════════════════ ANIMATIONS ═══════════════════════════════════════ */
const fadeUp = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const spin = keyframes`from{transform:rotate(0)}to{transform:rotate(360deg)}`;
const fogD = keyframes`0%{transform:translate(0,0);opacity:0.3}50%{transform:translate(15px,8px);opacity:0.45}100%{transform:translate(0,0);opacity:0.3}`;
const shieldPulse = keyframes`0%,100%{filter:drop-shadow(0 0 8px ${() => C.accent}40)}50%{filter:drop-shadow(0 0 18px ${() => C.accent}70)}`;
const horcruxGlow = keyframes`0%,100%{box-shadow:0 0 4px ${() => C.accent}30}50%{box-shadow:0 0 12px ${() => C.accent}60}`;
const wandSpark = keyframes`0%,100%{opacity:0.2;transform:translateY(0)}50%{opacity:0.6;transform:translateY(-3px)}`;
const dhRotate = keyframes`0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}`;
const stonePulse = keyframes`0%,100%{filter:drop-shadow(0 0 4px #ff335530)}50%{filter:drop-shadow(0 0 12px #ff335560)}`;
const stairShift = keyframes`0%{transform:translateX(0)}50%{transform:translateX(12px)}100%{transform:translateX(0)}`;
const stairShiftR = keyframes`0%{transform:translateX(0)}50%{transform:translateX(-12px)}100%{transform:translateX(0)}`;
const torchFlicker = keyframes`0%,100%{opacity:0.15;r:4}25%{opacity:0.25;r:5}50%{opacity:0.12;r:3.5}75%{opacity:0.22;r:4.5}`;
const paintingSway = keyframes`0%,100%{transform:rotate(0deg)}25%{transform:rotate(0.3deg)}75%{transform:rotate(-0.3deg)}`;

/* ═══════════════════════ STYLED ═══════════════════════════════════════════ */
const Section = styled.section`position:relative;min-height:100vh;background:transparent;padding:4rem 1.5rem;overflow:hidden;`;
const Mist = styled.div<{$p:string}>`position:absolute;${({$p})=>$p==="tl"?"top:5%;left:-5%;":"bottom:5%;right:-5%;"}width:220px;height:220px;border-radius:50%;background:radial-gradient(circle,rgba(139,92,246,0.035),transparent 70%);filter:blur(35px);animation:${fogD} 14s ease-in-out infinite;pointer-events:none;`;
const Inner = styled.div`position:relative;z-index:1;max-width:960px;margin:0 auto;animation:${fadeUp} 0.5s ease both;`;
const LoadBox = styled.div`display:flex;flex-direction:column;align-items:center;gap:1rem;padding:6rem;color:${() => C.d};font-family:"JetBrains Mono",monospace;font-size:0.75rem;`;
const Spin = styled.div`width:22px;height:22px;border:2px solid ${() => C.p}30;border-top:2px solid ${() => C.p};border-radius:50%;animation:${spin} 0.8s linear infinite;`;

/* Header */
const HeaderArea = styled.div`text-align:center;margin-bottom:2rem;`;
const Oath = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.52rem;color:${() => C.d};letter-spacing:0.15em;text-transform:uppercase;margin-bottom:0.3rem;`;
const PageTitle = styled.h1`font-family:"Cinzel",serif;font-size:1.8rem;font-weight:700;color:${() => C.t};margin-bottom:0.3rem;letter-spacing:0.02em;`;
const PageSub = styled.p`font-family:"Raleway",sans-serif;font-size:0.8rem;color:${() => C.m};`;
const SecHead = styled.h2`font-family:"Cinzel",serif;font-size:0.9rem;font-weight:600;color:${() => C.t};margin:1.5rem 0 0.6rem;padding-left:0.4rem;border-left:3px solid ${() => C.accent}40;display:flex;align-items:center;gap:0.4rem;letter-spacing:0.02em;`;

/* Deathly Hallows */
const DHSvg = styled.svg`width:30px;height:30px;margin:0.3rem auto;animation:${dhRotate} 20s linear infinite;`;

/* Wand */
const WandSvg = styled.svg`width:80px;height:14px;margin:0.5rem auto 0;.wand-spark{animation:${wandSpark} 1.5s ease-in-out infinite;}`;

/* Sorting Hat */
const SortHatSvg = styled.svg`width:22px;height:24px;flex-shrink:0;`;

/* Philosopher's Stone */
const PStoneSvg = styled.svg<{$achieved:boolean}>`width:42px;height:42px;flex-shrink:0;${({$achieved})=>$achieved && css`animation:${stonePulse} 2s ease-in-out infinite;`}`;

/* Trials Banner */
const TrialsBanner = styled.div`display:flex;align-items:center;gap:0.6rem;margin-bottom:0.6rem;padding:0.6rem 1rem;background:rgba(255,51,85,0.03);border:1px solid rgba(255,51,85,0.08);border-radius:8px;`;
const TrialsBannerText = styled.p`font-family:"Raleway",sans-serif;font-size:0.82rem;color:${() => C.m};font-style:italic;line-height:1.5;`;

/* ── Moving Staircase ── */
const StaircaseSvg = styled.svg<{$flip?: boolean}>`
  width:100%;height:34px;margin:2.1rem 0;opacity:0.75;overflow:visible;
  .stair-group{animation:${({$flip})=>$flip ? stairShiftR : stairShift} 8s ease-in-out infinite;}
  .torch-glow{animation:${torchFlicker} 3s ease-in-out infinite;}
`;

/* ── Portrait Frame (Moving Paintings) ── */
const FrameOuter = styled.div`
  position:relative;
  animation:${paintingSway} 6s ease-in-out infinite;
  border-radius:4px;overflow:hidden;
`;
const FrameTop = styled.div`
  display:flex;align-items:center;justify-content:center;gap:0.3rem;
  padding:0.35rem 0.6rem;
  background:linear-gradient(180deg,${() => C.frame},${() => C.frameDark});
  border:1px solid ${() => C.frameLight}40;border-bottom:none;
  border-radius:4px 4px 0 0;
`;
const FrameCorner = styled.div<{$pos:string}>`
  width:8px;height:8px;border-radius:50%;
  background:${() => C.frameLight};opacity:0.3;
`;
const FrameTitle = styled.span`
  font-family:"Cinzel",serif;font-size:0.55rem;font-weight:600;
  color:${() => C.accent};letter-spacing:0.08em;text-transform:uppercase;opacity:0.7;
`;
const FrameInner = styled.div`
  padding:1.2rem;
  background:${() => C.card};
  border-left:3px solid ${() => C.frame}60;border-right:3px solid ${() => C.frame}60;
`;
const FrameBottom = styled.div`
  height:6px;
  background:linear-gradient(0deg,${() => C.frame},${() => C.frameDark});
  border:1px solid ${() => C.frameLight}40;border-top:none;
  border-radius:0 0 4px 4px;
`;

/* Cards */
const Cd = styled.div`padding:1.2rem;background:${() => C.card};border:1px solid ${() => C.b};border-radius:12px;transition:border-color 0.3s;&:hover{border-color:${() => C.p}12;}`;

/* Guardian Patronus */
const GuardianRow = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:0.5rem;@media(max-width:600px){grid-template-columns:1fr;}`;
const ShieldWrap = styled(Cd)`display:flex;align-items:center;gap:1.2rem;justify-content:center;`;
const ShieldSvg = styled.svg`width:110px;height:130px;animation:${shieldPulse} 3s ease-in-out infinite;`;
const ShieldMeta = styled.div`display:flex;flex-direction:column;`;
const ShieldRating = styled.div`font-family:"Cinzel",serif;font-size:2.2rem;font-weight:700;color:${() => C.gold};text-shadow:0 0 12px ${() => C.gold}40;`;
const ShieldSubtitle = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.52rem;color:${() => C.d};margin-bottom:0.2rem;`;
const ShieldPct = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.65rem;color:${() => C.gold};font-weight:600;`;

/* Top% card */
const TopCard = styled(Cd)`position:relative;overflow:hidden;`;
const TopLabel = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.52rem;color:${() => C.d};position:relative;z-index:1;`;
const TopValue = styled.div`font-family:"Cinzel",serif;font-size:2.4rem;font-weight:700;color:${() => C.accent};text-shadow:0 0 10px ${() => C.accent}30;position:relative;z-index:1;`;
const TopPctNote = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.48rem;color:${() => C.m};position:relative;z-index:1;margin-bottom:0.3rem;`;
const HistoWrap = styled.div`position:absolute;bottom:0;right:0;top:0;width:55%;display:flex;align-items:flex-end;gap:3px;padding:10px 12px 10px 0;opacity:0.75;z-index:2;`;
const HistoBar = styled.div`flex:1;min-width:0;border-radius:2px 2px 0 0;cursor:none;transition:opacity 0.2s ease,transform 0.2s ease;&:hover{transform:scaleY(1.06);}`;

/* Profile sidebar */
const TopGrid = styled.div`display:grid;grid-template-columns:280px 1fr;gap:1rem;margin-bottom:0.5rem;@media(max-width:768px){grid-template-columns:1fr;}`;
const Sidebar = styled(Cd)``;
const PTop = styled.div`display:flex;gap:0.8rem;align-items:center;margin-bottom:0.6rem;`;
const Ava = styled.img`width:52px;height:52px;border-radius:8px;object-fit:cover;border:1px solid ${() => C.b};`;
const PName = styled.h3`font-family:"Cinzel",serif;font-size:0.95rem;font-weight:700;color:${() => C.t};display:flex;align-items:center;gap:0.25rem;`;
const V = styled.span`color:${() => C.p};font-size:0.5rem;`;
const PUser = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.6rem;color:${() => C.m};`;
const PRank = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.6rem;color:${() => C.d};& b{color:${() => C.t};}`;
const PAbout = styled.p`font-family:"Raleway",sans-serif;font-size:0.72rem;color:${() => C.m};margin-bottom:0.6rem;line-height:1.5;`;
const PMeta = styled.div`display:flex;flex-direction:column;gap:0.25rem;margin-bottom:0.7rem;`;
const Mi = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.55rem;color:${() => C.m};& a{color:${() => C.p};text-decoration:none;}`;
const SH = styled.h4`font-family:"Cinzel",serif;font-size:0.75rem;color:${() => C.t};margin:0.8rem 0 0.3rem;font-weight:600;`;
const LR = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.55rem;color:${() => C.m};display:flex;align-items:center;gap:0.4rem;margin-bottom:0.15rem;`;
const LT = styled.span`background:rgba(255,255,255,0.04);padding:0.1rem 0.35rem;border-radius:3px;color:${() => C.t};font-size:0.52rem;`;
const SL = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.5rem;color:${() => C.hard};margin:0.35rem 0 0.15rem;`;
const STags = styled.div`display:flex;flex-wrap:wrap;gap:0.2rem;`;
const ST = styled.span`font-family:"JetBrains Mono",monospace;font-size:0.48rem;background:rgba(255,255,255,0.03);padding:0.1rem 0.35rem;border-radius:3px;color:${() => C.m};`;

/* Contest */
const RCol = styled.div`display:flex;flex-direction:column;gap:0.8rem;`;
const CRow = styled.div`display:flex;gap:1.4rem;flex-wrap:wrap;margin-bottom:0.6rem;`;
const CB = styled.div``;
const CLabel = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.48rem;color:${() => C.d};margin-bottom:0.15rem;letter-spacing:0.05em;`;
const Big = styled.div`font-family:"Cinzel",serif;font-size:1.5rem;font-weight:700;color:${() => C.t};`;
const CVal = styled.div`font-family:"Cinzel",serif;font-size:0.95rem;font-weight:700;color:${() => C.t};`;
const Sub = styled.span`font-size:0.55rem;color:${() => C.d};font-weight:400;`;
const Guard = styled.span<{$isGuardian: boolean}>`font-family:"JetBrains Mono",monospace;font-size:0.68rem;font-weight:700;
  ${({$isGuardian}) => $isGuardian
    ? css`color:${() => C.accent};text-shadow:0 0 8px ${() => C.accent}50;`
    : css`color:${() => C.med};`}`;
const ChartWrap = styled.div`position:relative;`;

/* Solved / Potions */
const R2 = styled.div`display:grid;grid-template-columns:1.2fr 0.8fr;gap:1rem;margin-bottom:1rem;align-items:stretch;& > *{height:100%;}@media(max-width:900px){grid-template-columns:1fr;}`;
const SolvedFlex = styled.div`display:flex;align-items:center;gap:1.2rem;justify-content:center;flex-wrap:wrap;`;
const DArc = styled.circle`transform:rotate(-90deg);transform-origin:75px 75px;transition:stroke-dasharray 1s ease;`;
const PotionRow = styled.div`display:flex;gap:1rem;align-items:flex-end;`;
const PotionWrap = styled.div`display:flex;flex-direction:column;align-items:center;gap:0.2rem;
  .potion-bubble{animation:${keyframes`0%{transform:translateY(0);opacity:0.4}100%{transform:translateY(-6px);opacity:0}`} 2s ease-out infinite;}
`;
const PotionLabel = styled.div<{$c:string}>`font-family:"JetBrains Mono",monospace;font-size:0.52rem;color:${({$c})=>$c};font-weight:600;`;
const PotionCount = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.58rem;font-weight:700;color:${() => C.t};`;
const PotionTotal = styled.span`font-size:0.44rem;color:${() => C.d};font-weight:400;`;

/* Badges */
const BadgeHeader = styled.div`font-family:"Cinzel",serif;font-size:0.85rem;color:${() => C.t};margin-bottom:0.6rem;display:flex;align-items:center;& b{margin-left:0.2rem;}`;
const BadgeArrow = styled.span`margin-left:auto;font-size:0.9rem;color:${() => C.d};cursor:pointer;`;
const BRow = styled.div`display:flex;gap:0.4rem;margin-bottom:0.5rem;flex-wrap:wrap;`;
const BI = styled.div`width:50px;height:50px;border-radius:10px;background:rgba(255,255,255,0.03);border:1px solid ${() => C.b};display:flex;align-items:center;justify-content:center;& img{width:34px;height:34px;object-fit:contain;}& span{font-size:1.3rem;}`;
const BR = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.52rem;color:${() => C.d};& b{color:${() => C.med};}`;

/* Best Contests Table */
const BestTable = styled.table`width:100%;border-collapse:collapse;font-family:"JetBrains Mono",monospace;font-size:0.58rem;`;
const BTh = styled.th`text-align:left;padding:0.6rem 0.75rem;color:${() => C.m};font-size:0.58rem;letter-spacing:0.05em;text-transform:uppercase;border-bottom:1px solid ${() => C.b};font-weight:500;`;
const BTr = styled.tr<{$isFirst:boolean}>`
  ${({$isFirst}) => $isFirst && css`background:rgba(255,215,0,0.04);`}
  &:hover{background:rgba(139,92,246,0.04);}
`;
const MEDAL = ["#ffd700", "#cfd4dc", "#c98a4b"];   // gold, silver, bronze
const BTd = styled.td<{$rank:number}>`
  padding:0.7rem 0.75rem;
  font-size:0.8rem;
  color:${({$rank})=>$rank < 3 ? MEDAL[$rank] : C.m};
  font-weight:${({$rank})=>$rank === 0 ? 700 : $rank < 3 ? 600 : 400};
  border-bottom:1px solid rgba(255,255,255,0.03);
`;

const Medal = styled.span<{$rank:number}>`
  display:inline-flex;align-items:center;justify-content:center;
  width:20px;height:20px;border-radius:50%;
  font-family:"Cinzel",serif;font-size:0.66rem;font-weight:700;
  color:${({$rank})=>$rank < 3 ? "#1a1206" : C.d};
  background:${({$rank})=>$rank < 3 ? MEDAL[$rank] : "rgba(255,255,255,0.05)"};
  box-shadow:${({$rank})=>$rank < 3 ? `0 0 10px ${MEDAL[$rank]}44` : "none"};
`;

/* Stats */
const SG = styled.div`display:flex;gap:1.8rem;justify-content:center;margin-bottom:0.8rem;flex-wrap:wrap;&:last-child{margin-bottom:0;}`;
const GlassRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: clamp(0.8rem, 3vw, 2rem);
  flex-wrap: wrap;
  margin-bottom: 1.2rem;
  padding-bottom: 1.1rem;
  border-bottom: 1px solid rgba(139, 92, 246, 0.12);
`;

const SI = styled.div`text-align:center;min-width:65px;`;
const StatNum = styled.div<{$c:string}>`font-family:"Cinzel",serif;font-size:1.15rem;font-weight:700;color:${({$c})=>$c};& small{font-size:0.55rem;font-weight:400;color:${() => C.d};font-family:"JetBrains Mono",monospace;}`;
const StatLabel = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.48rem;color:${() => C.d};margin-top:0.1rem;`;

/* Horcrux Milestones */
const HorcruxGrid = styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:0.8rem;@media(max-width:900px){grid-template-columns:repeat(3,1fr);}@media(max-width:560px){grid-template-columns:repeat(2,1fr);}`;
const HorcruxItem = styled.div<{$done:boolean}>`display:flex;flex-direction:column;align-items:center;gap:0.35rem;padding:1rem 0.7rem;border-radius:8px;border:1px solid ${({$done})=>$done ? `${() => C.accent}30` : C.b};
  background:${({$done})=>$done ? `rgba(255,215,0,0.03)` : "transparent"};
  ${({$done})=>$done && css`animation:${horcruxGlow} 3s ease-in-out infinite;`}
  transition:all 0.3s;&:hover{transform:translateY(-2px);}`;
const HorcruxIcon = styled.div<{$done:boolean}>`font-size:1.7rem;${({$done})=>!$done && "filter:grayscale(1) opacity(0.4);"}`;
const HorcruxName = styled.div<{$done:boolean}>`font-family:"Cinzel",serif;font-size:0.74rem;font-weight:600;color:${({$done})=>$done ? C.accent : C.d};text-align:center;line-height:1.3;`;
const HorcruxDesc = styled.div`font-family:"Raleway",sans-serif;font-size:0.62rem;color:${() => C.m};text-align:center;line-height:1.45;`;
const HorcruxCheck = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.5rem;color:${() => C.easy};letter-spacing:0.1em;font-weight:700;margin-top:0.1rem;`;

const YearBlock = styled.div`
  position:relative;
  margin-bottom:1.1rem;
  padding:0.85rem 0.9rem 0.7rem;
  border:1px solid ${() => C.accent}22;
  border-radius:10px;
  background:rgba(255,255,255,0.012);
  transition:border-color 0.3s ease, background 0.3s ease;

  &:hover{
    border-color:${() => C.accent}44;
    background:rgba(255,255,255,0.022);
  }
`;
const YearLabel = styled.div`
  position:absolute;
  top:-0.55rem; left:0.85rem;
  padding:0 0.4rem;
  background:${() => C.card};
  font-family:"Cinzel",serif;
  font-size:0.7rem;
  font-weight:600;
  letter-spacing:0.2em;
  color:${() => C.accent};
  opacity:0.9;
`;

/* Heatmap */
const HH = styled.div`display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:0.5rem;margin-bottom:0.6rem;font-family:"JetBrains Mono",monospace;font-size:0.6rem;color:${() => C.m};& b{color:${() => C.t};font-size:1rem;}`;
const HMeta = styled.span`font-size:0.48rem;color:${() => C.d};`;
const HmSvg = styled.svg`width:100%;height:auto;display:block;`;
const HmHolder = styled.div`position:relative;`;

const HeatTip = styled.div`
  position:absolute;
  transform:translate(-50%,-115%);
  z-index:6;
  padding:0.4rem 0.6rem;
  border-radius:6px;
  background:rgba(10,8,16,0.96);
  border:1px solid rgba(63,146,255,0.35);
  box-shadow:0 6px 18px rgba(0,0,0,0.6);
  font-family:"JetBrains Mono",monospace;
  font-size:0.56rem;
  color:${() => C.t};
  white-space:nowrap;
  pointer-events:none;
  b{color:#78bcff;font-size:0.7rem;}
  em{display:block;font-style:normal;color:${() => C.d};margin-top:0.12rem;}
`;

const Lair = styled.div`position:relative;height:0;`;

const FluffyPerch = styled.div`
  display:flex;flex-direction:column;align-items:center;gap:0.3rem;
  margin:0.2rem 0 0.8rem;
`;

const PerchNote = styled.span`
  font-family:"Caveat",cursive;font-size:0.9rem;color:${() => C.d};
`;

const SerpentTrail = styled.div`
  position:relative;height:34px;overflow:hidden;margin-bottom:-0.4rem;
`;

/* ── House theme picker ── */
const ThemeBar = styled.div`
  position:relative; z-index:2;
  max-width:960px; margin:0 auto 1.6rem;
  display:flex; flex-direction:column; align-items:center; gap:0.5rem;
`;

const ThemeBarLabel = styled.span`
  font-family:"JetBrains Mono",monospace; font-size:0.5rem;
  letter-spacing:0.26em; text-transform:uppercase; color:${() => C.d};
`;

const ThemeBarRow = styled.div`
  display:flex; gap:0.5rem; flex-wrap:wrap; justify-content:center;
`;

const HouseBtn = styled.button<{$on:boolean;$swatch:string}>`
  display:flex; align-items:center; gap:0.4rem;
  padding:0.4rem 0.8rem;
  border-radius:100px;
  cursor:none;
  transition:all 0.28s ease;
  background:${({$on,$swatch})=>$on ? `${$swatch}1c` : "rgba(255,255,255,0.02)"};
  border:1px solid ${({$on,$swatch})=>$on ? `${$swatch}88` : "rgba(255,255,255,0.07)"};
  box-shadow:${({$on,$swatch})=>$on ? `0 0 16px ${$swatch}33` : "none"};

  &:hover{
    border-color:${({$swatch})=>`${$swatch}aa`};
    transform:translateY(-1px);
  }
`;

const HouseCrest = styled.span`font-size:0.9rem;line-height:1;`;

const HouseName = styled.span`
  font-family:"Cinzel",serif; font-size:0.58rem; font-weight:600;
  letter-spacing:0.1em; color:${() => C.t}; white-space:nowrap;
  @media(max-width:620px){ display:none; }
`;

/* ── Vertical side rails ── */
const Rails = styled.div`
  position:absolute;
  top:0; bottom:0; left:0; right:0;
  width:100%;
  max-width:1280px;
  margin:0 auto;
  z-index:1;
  pointer-events:none;
  @media(max-width:1280px){ display:none; }
`;

const railBase = css`
  position:absolute; top:16%;
  display:flex; flex-direction:column; align-items:center; gap:1.1rem;
`;

const RailL = styled.div`${railBase} left:0;`;
const RailR = styled.div`${railBase} right:0;`;

const RailWord = styled.span<{$accent?:boolean}>`
  writing-mode:vertical-rl; text-orientation:mixed;
  font-family:"Cinzel",serif;
  font-size:clamp(0.85rem,1.5vw,1.15rem);
  font-weight:700;
  letter-spacing:0.22em;
  text-transform:uppercase;
  color:${({$accent})=>$accent ? C.accent : C.t};
  opacity:0.32;
`;

const RailRule = styled.span`
  width:1px; height:56px;
  background:linear-gradient(180deg, transparent, ${() => C.accent}66, transparent);
`;

const RailStat = styled.span`
  writing-mode:vertical-rl; text-orientation:mixed;
  font-family:"JetBrains Mono",monospace;
  font-size:0.56rem;
  letter-spacing:0.16em;
  text-transform:uppercase;
  color:${() => C.d};
  b{color:${() => C.accent};font-weight:600;}
`;

const HistoTip = styled.div`
  position:absolute;
  bottom:calc(100% + 6px);
  transform:translateX(-50%);
  z-index:6;
  padding:0.4rem 0.65rem;
  border-radius:6px;
  background:rgba(10,8,16,0.96);
  border:1px solid rgba(255,215,0,0.3);
  box-shadow:0 6px 18px rgba(0,0,0,0.6);
  font-family:"JetBrains Mono",monospace;
  font-size:0.54rem;
  color:${() => C.m};
  white-space:nowrap;
  pointer-events:none;
  strong{display:block;color:${() => C.t};font-size:0.62rem;margin-bottom:0.1rem;}
  span{color:${() => C.accent};}
`;

const ChartTip = styled.div`
  position:absolute;
  transform:translate(-50%,-118%);
  z-index:6;
  padding:0.45rem 0.7rem;
  border-radius:6px;
  background:rgba(10,8,16,0.96);
  border:1px solid rgba(255,215,0,0.32);
  box-shadow:0 6px 18px rgba(0,0,0,0.6);
  font-family:"JetBrains Mono",monospace;
  font-size:0.56rem;
  color:${() => C.m};
  white-space:nowrap;
  pointer-events:none;
  strong{display:block;color:${() => C.t};font-size:0.62rem;margin-bottom:0.15rem;}
  span{color:${() => C.accent};}
`;

const HeatLegend = styled.div`display:flex;align-items:center;gap:4px;justify-content:flex-end;margin-top:0.5rem;font-family:"JetBrains Mono",monospace;font-size:0.42rem;color:${() => C.d};`;
const HeatBox = styled.div`width:11px;height:11px;border-radius:50%;`;

/* CTA */
const CTARow = styled.div`display:flex;gap:0.8rem;justify-content:center;flex-wrap:wrap;margin:1.5rem 0 0.6rem;`;
const Btn = styled.a<{$s?:boolean}>`font-family:"JetBrains Mono",monospace;font-size:0.65rem;padding:0.55rem 1.1rem;border-radius:7px;text-decoration:none;transition:all 0.3s ease;
  ${({$s})=>$s
    ?css`color:${() => C.d};border:1px solid ${() => C.p}10;background:transparent;&:hover{border-color:${() => C.p}22;color:${() => C.m};}`
    :css`color:${() => C.t};background:${() => C.p}0D;border:1px solid ${() => C.p}15;&:hover{background:${() => C.p}15;transform:translateY(-2px);}`}`;
const Live = styled.p`text-align:center;font-family:"JetBrains Mono",monospace;font-size:0.46rem;color:${() => C.d};`;


